import React, { useMemo } from 'react';

import { get } from 'lodash';

import { SelectField, TextField, useRecordContext } from 'react-admin';

import { Grid, Box, Typography } from '@mui/material';

import languages, { Languages } from '../../../../constants/languages';
import controlTypes, { directControlTypeChoices, indirectControlTypeChoices } from '../../../../constants/controlTypes';
import applyChoices from '../../applyChoices';
import { ControlTypeParameters } from '../../questionParameters';

const TITLE = {
  [languages.SV]: 'På vilket sätt äger eller kontrollerar personen företaget?',
  [languages.EN]: 'How is control exercised?',
  [languages.DE]: 'Wie wird die Kontrolle ausgeübt?',
};

const OWNERSHIP_LABEL = {
  [languages.SV]: 'Ägarandel i %',
  [languages.EN]: 'Ownership in %',
  [languages.DE]: 'Beteiligung in %',
};

const DIRECT_CONTROL_LABEL = {
  [languages.SV]: 'Direkt ägande',
  [languages.EN]: 'Direct control',
  [languages.DE]: 'Direkte Kontrolle',
};

const INDIRECT_CONTROL_LABEL = {
  [languages.SV]: 'Indirekt ägande',
  [languages.EN]: 'Indirect control',
  [languages.DE]: 'Indirekte Kontrolle',
};

const ControlType = ({
  source,
  extraParameters,
}: {
  source?: string;
  language?: Languages;
  extraParameters?: ControlTypeParameters;
}) => {
  const record = useRecordContext();

  const language = record?.language as Languages;

  const controlType = get(record, `${source ?? 'controlType'}`);
  const sx = get(record, 'sx', {});

  const shouldStateShare = useMemo(
    () => [
      controlTypes.SHAREHOLDER,
      controlTypes.VOTING_RIGHT,
    ].includes(controlType?.type),
    [controlType?.type],
  );

  const { fields } = extraParameters ?? {};

  return (
    <>
      <Grid
        size={{
          xs: 12,
          md: 3,
        }}
      >
        <Box display="flex" flexDirection="column">
          <Typography variant="label">
            {fields?.direction?.label ?? extraParameters?.label ?? TITLE[language]}
          </Typography>
          <SelectField
            choices={applyChoices(
              [
                { name: DIRECT_CONTROL_LABEL[language], id: 'yes' },
                { name: INDIRECT_CONTROL_LABEL[language], id: 'no' },
              ],
              fields?.direction?.options,
              fields?.direction?.choiceLabels,
            )}
            source={`${source ?? 'controlType'}.direct`}
            emptyText="-"
            sx={sx}
          />
        </Box>
      </Grid>
      <Grid
        size={{
          xs: 12,
          md: 3,
        }}
      >
        <Box display="flex" flexDirection="column">
          <Typography variant="label">
            {(controlType?.type === 'other' ? fields?.other?.label : extraParameters?.inputLabel)
              ?? TITLE[language]}
          </Typography>
          {controlType?.type !== 'other' && (
            <SelectField
              choices={applyChoices(
                controlType?.direct === 'yes'
                  ? directControlTypeChoices(language)
                  : indirectControlTypeChoices(language),
                extraParameters?.options,
                extraParameters?.choiceLabels,
              )}
              source={`${source ?? 'controlType'}.type`}
              emptyText="-"
              sx={sx}
            />
          )}
          {controlType?.type === 'other' && (
            <TextField
              sx={{
                ...sx,
                wordWrap: 'break-word',
              }}
              record={{ value: controlType?.other }}
              source="value"
              emptyText="-"
            />
          )}
        </Box>
      </Grid>
      {shouldStateShare && (
        <Grid
          size={{
            xs: 12,
            md: 3,
          }}
        >
          <Box display="flex" flexDirection="column">
            <Typography variant="label">{fields?.ownership?.label ?? OWNERSHIP_LABEL[language]}</Typography>
            <TextField
              source={`${source ?? 'controlType'}.ownership`}
              emptyText="-"
              sx={sx}
            />
          </Box>
        </Grid>
      )}
    </>
  );
};

ControlType.ValueField = ControlType;

ControlType.Nested = ControlType;

export default ControlType;
